import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { toast } from 'sonner';
import { Loader2, Power, PowerOff, CalendarClock } from 'lucide-react';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};
const dur = (s) => (s ? `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h` : '—');

export default function OilAdminLiveControl({ seasonId, onChanged }) {
  const [d, setD] = useState(null);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/oil/season/${seasonId}/live-status`);
      setD(r.data);
      setDate((r.data.starts_at || '').slice(0, 10));
    } catch { toast.error('Erro ao ler o estado do evento.'); }
  }, [seasonId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    if (reason.trim().length < 3) { toast.error('Informe o motivo.'); return; }
    setBusy(true);
    try { const r = await fn(); toast.success(msg(r?.data)); setReason(''); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro na operação.'); }
    setBusy(false);
  };

  if (!d) return <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  const toggleVisible = async (v) => {
    setBusy(true);
    try {
      await api.patch(`/admin/oil/season/${seasonId}/visibility`, { public_visible: v });
      toast.success(v ? 'Evento visível para o público.' : 'Evento escondido do público.');
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao mudar a visibilidade.'); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border-2 p-3" data-testid="oil-live-control"
      style={{ borderColor: d.live ? '#10b981' : '#f43f5e' }}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-slate-50 px-3 py-2"
        data-testid="oil-visibility-row">
        <div>
          <p className="text-xs font-black uppercase text-slate-700">Mostrar evento para o público</p>
          <p className="text-[11px] text-slate-500">
            {d.public_visible
              ? (d.ended ? 'Ligado: Season encerrada aparece no site em modo consulta (aviso ENCERRADA + rankings).'
                : 'Ligado: o evento aparece no site normalmente.')
              : 'Desligado: o evento some do site e da Conta do Cliente, encerrado ou não.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-black ${d.public_visible ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="oil-visibility-label">
            {d.public_visible ? 'VISÍVEL' : 'OCULTO'}
          </span>
          <Switch checked={!!d.public_visible} disabled={busy} onCheckedChange={toggleVisible} data-testid="oil-visibility-switch" />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-black uppercase text-slate-600">Evento no site</p>
          <p className={`text-lg font-black ${d.live ? 'text-emerald-600' : 'text-rose-600'}`}
            data-testid="oil-live-badge">
            {d.live ? 'NO AR' : d.paused ? 'DESATIVADO' : d.ended ? 'ENCERRADO' : d.seconds_to_start ? 'AGENDADO' : 'FORA DO AR'}
          </p>
          <p className="text-[11px] text-slate-500" data-testid="oil-live-window">
            Estreia {fmtDate(d.starts_at)} · termina {fmtDate(d.ends_at)} · duração fixa de {d.duration_days} dias
            {d.seconds_to_start ? ` · começa em ${dur(d.seconds_to_start)}` : ''}
            {d.paused_total_seconds ? ` · ${dur(d.paused_total_seconds)} de pausa já compensados no fim` : ''}
          </p>
          {d.paused && d.paused_reason && (
            <p className="text-[11px] font-bold text-rose-600">Motivo: {d.paused_reason}</p>
          )}
        </div>
        {d.paused ? (
          <Button size="sm" disabled={busy} data-testid="oil-live-resume"
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            onClick={() => act(() => api.post(`/admin/oil/season/${seasonId}/resume`, { reason }),
              (r) => `Evento reativado. Fim empurrado em ${dur(r?.stopped_seconds)}.`)}>
            <Power className="mr-1 h-3.5 w-3.5" /> Reativar evento
          </Button>
        ) : (
          <Button size="sm" variant="destructive" disabled={busy} data-testid="oil-live-pause"
            onClick={() => act(() => api.post(`/admin/oil/season/${seasonId}/pause`, { reason }),
              () => 'Evento desativado: saiu do site e o cronômetro parou.')}>
            <PowerOff className="mr-1 h-3.5 w-3.5" /> Desativar evento
          </Button>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 border-t pt-2">
        <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="oil-live-reason"
          placeholder="Motivo (obrigatório para desativar, reativar ou mudar a data)"
          className="h-9 min-w-64 flex-1 rounded-lg border px-2 text-xs" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="oil-live-date"
          className="h-9 rounded-lg border px-2 text-xs" />
        <Button size="sm" variant="outline" disabled={busy || !date} data-testid="oil-live-schedule"
          onClick={() => act(() => api.patch(`/admin/oil/season/${seasonId}/schedule`,
            { starts_at: date, reason }), (r) => `Estreia em ${fmtDate(r?.starts_at)} · fim ${fmtDate(r?.ends_at)}.`)}>
          <CalendarClock className="mr-1 h-3.5 w-3.5" /> Aplicar data de início
        </Button>
      </div>
      <p className="mt-1 text-[10px] text-slate-500">
        Desativado ou antes da estreia, o OIL TYCOON não aparece no site e quem entrar pela URL vê
        "evento indisponível". O tempo parado não conta: ao reativar, a data de fim é empurrada.
      </p>
    </div>
  );
}
